/**
 * Offline Queue Service
 * 
 * Handles offline logging with IndexedDB storage and Background Sync
 * 
 * Rules:
 * - No offline AI calls
 * - No offline OCR
 * - Server is source of truth
 * - Failed sync retries automatically
 */

const DB_NAME = 'CoreTrakOfflineQueue';
const DB_VERSION = 2; // Bumped to force onupgradeneeded after storageCleanup DB deletion
const STORE_NAME = 'pendingEntries';

export type EntryType = 'nutrition' | 'workout';

export interface PendingEntry {
  id: string; // Unique ID for the pending entry
  type: EntryType;
  userId: string;
  entryData: Record<string, unknown>; // The entry data to sync
  date: string; // YYYY-MM-DD
  timestamp: number; // When it was created
  retryCount: number; // Number of sync attempts
  lastRetryAt?: number; // Last retry timestamp
}

let db: IDBDatabase | null = null;

/**
 * Initialize IndexedDB
 * Bug Fix: Handle connection errors and retry if connection is closed
 */
async function initDB(): Promise<IDBDatabase> {
  // Check if existing connection is still valid
  if (db) {
    try {
      // Try to access the database to verify it's still open
      // If it throws, the connection is closed
      void db.objectStoreNames; // Access property to verify connection is open
      return db;
    } catch (error) {
      console.warn('[OfflineQueue] Existing DB connection closed, reopening:', error);
      db = null;
    }
  }

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      const error = new Error('Failed to open IndexedDB');
      console.error('[OfflineQueue] IndexedDB open error:', error);
      reject(error);
    };

    request.onsuccess = () => {
      db = request.result;
      
      // Handle connection close events
      db.onerror = (event) => {
        console.error('[OfflineQueue] IndexedDB error:', event);
      };
      
      db.onclose = () => {
        console.warn('[OfflineQueue] IndexedDB connection closed');
        db = null;
      };
      
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = (event.target as IDBOpenDBRequest).result;

      // Create object store if it doesn't exist
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        const store = database.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('userId', 'userId', { unique: false });
        store.createIndex('type', 'type', { unique: false });
        store.createIndex('timestamp', 'timestamp', { unique: false });
      }
    };
  });
}

/**
 * Check if device is online
 */
export function isOnline(): boolean {
  return navigator.onLine;
}

/**
 * Add entry to offline queue
 * Bug Fix: Enhanced error handling and retry logic
 */
export async function queueEntry(
  type: EntryType,
  userId: string,
  entryData: Record<string, unknown>,
  date?: string,
): Promise<PendingEntry> {
  const entryDate = date || new Date().toISOString().split('T')[0];

  // Retry logic for IndexedDB operations
  let retries = 0;
  const maxRetries = 3;
  let lastError: Error | null = null;

  while (retries < maxRetries) {
    try {
      // Generate new ID for each retry attempt (in case of duplicate)
      const pendingEntry: PendingEntry = {
        id: `${type}_${userId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        type,
        userId,
        entryData,
        date: entryDate,
        timestamp: Date.now(),
        retryCount: 0,
      };

      const database = await initDB();

      // Use async/await pattern instead of Promise constructor for better error handling
      // CRITICAL FIX: Ensure transaction completes before resolving
      const result = await new Promise<PendingEntry>((resolve, reject) => {
        const transaction = database.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.add(pendingEntry);

        // CRITICAL: Wait for transaction to complete, not just the add request
        transaction.oncomplete = () => {
          console.log('[OfflineQueue] Entry queued and transaction completed:', pendingEntry.id);
          resolve(pendingEntry);
        };

        request.onsuccess = () => {
          // Request succeeded, but wait for transaction.oncomplete to ensure it's persisted
          console.log('[OfflineQueue] Add request succeeded, waiting for transaction completion:', pendingEntry.id);
        };

        request.onerror = (event) => {
          const error = (event.target as IDBRequest).error;
          console.error('[OfflineQueue] Failed to queue entry:', error);
          
          // Check if it's a constraint error (duplicate ID) - will retry with new ID
          if (error && error.name === 'ConstraintError') {
            console.log('[OfflineQueue] Duplicate ID detected, will retry with new ID');
            reject(new Error('CONSTRAINT_ERROR')); // Special error code for retry
            return;
          }
          
          reject(new Error(`Failed to queue entry: ${error?.message || 'Unknown error'}`));
        };

        transaction.onerror = (event) => {
          const error = (event.target as IDBTransaction).error;
          console.error('[OfflineQueue] Transaction error:', error);
          reject(new Error(`Transaction failed: ${error?.message || 'Unknown error'}`));
        };

        transaction.onabort = () => {
          console.error('[OfflineQueue] Transaction aborted');
          reject(new Error('Transaction was aborted'));
        };
      });

      // Success - return the result
      return result;
    } catch (error) {
      retries++;
      lastError = error instanceof Error ? error : new Error(String(error));
      console.error(`[OfflineQueue] Queue attempt ${retries} failed:`, lastError);
      
      // If it's a constraint error, retry immediately with new ID
      if (lastError.message === 'CONSTRAINT_ERROR') {
        if (retries < maxRetries) {
          // Small delay to ensure previous transaction is fully closed
          await new Promise(resolve => setTimeout(resolve, 50));
          // Reset DB connection and retry
          db = null;
          continue;
        }
      }
      
      if (retries >= maxRetries) {
        throw new Error(`Failed to queue entry after ${maxRetries} attempts: ${lastError.message}`);
      }
      
      // Wait a bit before retrying (exponential backoff)
      await new Promise(resolve => setTimeout(resolve, 100 * retries));
      
      // Reset DB connection for retry to ensure fresh connection
      db = null;
    }
  }

  throw lastError || new Error('Failed to queue entry after all retries');
}

/**
 * Get all pending entries for a user
 */
export async function getPendingEntries(userId: string): Promise<PendingEntry[]> {
  const database = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('userId');
    const request = index.getAll(userId);

    request.onsuccess = () => {
      const entries = request.result.sort((a, b) => a.timestamp - b.timestamp);
      resolve(entries);
    };

    request.onerror = () => {
      reject(new Error('Failed to get pending entries'));
    };
  });
}

/**
 * Get pending entries by type
 */
export async function getPendingEntriesByType(
  userId: string,
  type: EntryType,
): Promise<PendingEntry[]> {
  const database = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('userId');
    const request = index.getAll(userId);

    request.onsuccess = () => {
      const entries = request.result
        .filter((entry) => entry.type === type)
        .sort((a, b) => a.timestamp - b.timestamp);
      resolve(entries);
    };

    request.onerror = () => {
      reject(new Error('Failed to get pending entries'));
    };
  });
}

/**
 * Remove entry from queue after successful sync
 */
export async function removePendingEntry(entryId: string): Promise<void> {
  const database = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(entryId);

    request.onsuccess = () => {
      console.log('[OfflineQueue] Entry removed:', entryId);
      resolve();
    };

    request.onerror = () => {
      reject(new Error('Failed to remove pending entry'));
    };
  });
}

/**
 * Update retry count for failed sync
 */
export async function updateRetryCount(entryId: string, retryCount: number): Promise<void> {
  const database = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const getRequest = store.get(entryId);

    getRequest.onsuccess = () => {
      const entry = getRequest.result;
      if (entry) {
        entry.retryCount = retryCount;
        entry.lastRetryAt = Date.now();
        const updateRequest = store.put(entry);
        updateRequest.onsuccess = () => resolve();
        updateRequest.onerror = () => reject(new Error('Failed to update retry count'));
      } else {
        resolve();
      }
    };

    getRequest.onerror = () => {
      reject(new Error('Failed to get entry for retry update'));
    };
  });
}

/**
 * Clear all pending entries (for testing/debugging)
 */
export async function clearAllPendingEntries(): Promise<void> {
  const database = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.clear();

    request.onsuccess = () => {
      console.log('[OfflineQueue] All entries cleared');
      resolve();
    };

    request.onerror = () => {
      reject(new Error('Failed to clear entries'));
    };
  });
}

/**
 * Get count of pending entries
 */
export async function getPendingCount(userId: string): Promise<number> {
  const entries = await getPendingEntries(userId);
  return entries.length;
}

